// =============================================================================
// features/admin/feature-flags/AudienceEditorForm.tsx — Corpo do formulário de
// audience (4 eixos + resumo). Extraído de AudienceEditorDrawer por tamanho.
// =============================================================================

import * as React from 'react';

import { CityMultiCombobox } from '../../../components/comboboxes/CityMultiCombobox';
import type { FeatureFlagStatus } from '../../../hooks/admin/useFeatureFlags.types';
import { ROLE_LABELS } from '../../../hooks/admin/useUsers.types';
import type { CityPublic } from '../../../hooks/useCitiesList';

import type { AudienceDraft } from './audienceImpact';
import { buildAudienceImpact } from './audienceImpact';
import { AudienceSummary } from './AudienceSummary';
import { OrganizationAxisInput } from './OrganizationAxisInput';
import { RoleAxisSelect } from './RoleAxisSelect';
import { UserAxisSelect } from './UserAxisSelect';

interface AudienceEditorFormProps {
  status: FeatureFlagStatus;
  draft: AudienceDraft;
  onChange: (draft: AudienceDraft) => void;
  cities: CityPublic[];
}

function FieldGroup({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <label
        className="font-sans font-semibold uppercase text-ink-3"
        style={{ fontSize: '0.68rem', letterSpacing: '0.1em' }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

export function AudienceEditorForm({
  status,
  draft,
  onChange,
  cities,
}: AudienceEditorFormProps): React.JSX.Element {
  const cityLabel = (id: string): string => cities.find((c) => c.id === id)?.name ?? id;
  const roleLabel = (key: string): string => ROLE_LABELS[key] ?? key;
  const impact = buildAudienceImpact(status, draft, { roleLabel, cityLabel });

  return (
    <div className="flex flex-col gap-6 px-6 py-6 flex-1">
      <FieldGroup label="Papéis">
        <RoleAxisSelect value={draft.roles} onChange={(roles) => onChange({ ...draft, roles })} />
      </FieldGroup>

      <FieldGroup label="Cidades">
        <CityMultiCombobox
          id="audience-cities"
          label=""
          cities={cities}
          value={draft.city_ids}
          onChange={(city_ids) => onChange({ ...draft, city_ids })}
          placeholder="Buscar cidade…"
        />
      </FieldGroup>

      <FieldGroup label="Organizações">
        <OrganizationAxisInput
          value={draft.organization_ids}
          onChange={(organization_ids) => onChange({ ...draft, organization_ids })}
        />
      </FieldGroup>

      <FieldGroup label="Usuários específicos">
        <UserAxisSelect
          value={draft.user_ids}
          onChange={(user_ids) => onChange({ ...draft, user_ids })}
        />
      </FieldGroup>

      <AudienceSummary impact={impact} />
    </div>
  );
}
